import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box } from '@mui/material';
import { CrmPaths } from 'app/configs/routesConfig/crm';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import { LoansIframePaths } from 'app/configs/routesConfig/loansIframe';
import ContentLayout from 'app/layouts/DefaultLayout/shared/ContentLayout';
import Skeleton from 'app/shared/components/common/Skeleton';
import SavingOverlay from 'app/shared/componentsRestyled/coreDesignSystem/SavingOverlay';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { CHECKLIST_RECALCULATING_STATUSES } from 'constants/checklists';
import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { FeatureFlag } from 'constants/featureFlags';
import { emptyLayoutConfig } from 'constants/layout';
import {
  LEGACY_LOAN_TAB_ROUTE_REDIRECTS,
  LoanApplicationTabRouteParam,
  LoanApplicationTabRouteParamInverted,
} from 'constants/loanApplication';
import { PAGE_CODES } from 'constants/pageCodes';
import { useChecklistRecalculationTimeout } from 'hooks/useChecklistRecalculationTimeout';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { getConditionalArray } from 'utils/data';

import { getMasterPermissionsToPage, selectIsClaimAllowed } from 'store/auth/selectors';
import {
  selectAreMasterCompanyPropertiesSettled,
  selectIsAiChatEnabled,
  selectIsCrmEnabledForCompany,
} from 'store/companies/selectors';
import { resetStateAction, setDefaultSettingsAction } from 'store/global/actions';
import {
  getChecklistsExecutionSnapshotsAction,
  getCompletenessChecklistIdsAction,
  getCompletenessDesignationAction,
  getDealDataByApplicationIdAction,
  getDealDataByLoanIdAction,
  resetDealDataAction,
  setAvailableTabsAction,
  setDealDataAction,
  triggerOptionalChecklistRecalculation,
} from 'store/loanApplications/actions';
import {
  selectDealFromApplication,
  selectIsConstructionFromPropertyAndLoanSummary,
  selectIsSavingLoanApplicationChanges,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectLoanApplicationChecklistResultsList,
  selectLoanApplicationControlUI,
  selectPrimaryBorrowerLastName,
  selectSubjectProperty,
} from 'store/loanApplications/selectors';

import AiAssistant from './components/AiAssistant';
import {
  useLoanApplicationApiInterceptor,
  usePendingChangesAuthorsSelection,
  usePricingStatus,
} from './hooks';
import {
  headerOptions,
  tabsWithoutContentScroll,
  tabsWithoutHorPadding,
} from './LoanApplication.constants';
import {
  checkIsApplicationAtLeadStage,
  deepObjFind,
  getAvailableTabs,
  getSubtabsFromChecklistResults,
} from './LoanApplication.utils';
import LoanApplicationContent from './LoanApplicationContent';
import { ShortcutOverlayConfig } from './LoanApplicationPage.constants';
import { shortcutsOverlayContainerStyles } from './LoanApplicationPage.styles';
import LoanEditorChatDrawer from './LoanEditorChatDrawer';
import MainHeaderContent from './LoanHeaders/MainHeaderContent';
import LoanNavigationTabs from './LoanNavigationTabs';
import ShortcutOverlayItem from './ShortcutOverlayItem';
import UniversalSearch from './UniversalSearch';
import { buildShortcutMap } from './UniversalSearch/builders/navigation.utils';
import KeyboardShortcutsPanel from './UniversalSearch/components/KeyboardShortcutsPanel';
import { getShortcutPrefix } from './UniversalSearch/UniversalSearch.utils';

const LoanApplicationPage = ({ isOpenInIframe = false }) => {
  const dispatch = useDispatch();

  useLoanApplicationApiInterceptor();

  const { tab: loanEditorActiveTab, applicationId, loanId, subtab } = useParams();

  usePendingChangesAuthorsSelection(applicationId);

  const resolvedLoanId = useLoanId();

  const companyId = useCompanyId();

  const { state: navigationState, pathname } = useLocation();

  const isInitialLoad = useRef(true);
  const isFromLoansIframe = useRef(
    LoansIframePaths.loansIframe.matchPath(navigationState?.pathFrom),
  );

  const history = useHistory();

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isShortcutsPanelOpen, setIsShortcutsPanelOpen] = useState(false);
  const [isModifierHeld, setIsModifierHeld] = useState(false);
  const [searchInitialQuery, setSearchInitialQuery] = useState('');

  const shortcutPrefix = useMemo(() => getShortcutPrefix(), []);

  const onOpenSearch = useCallback(() => {
    setSearchInitialQuery('');
    setIsSearchOpen(true);
  }, []);

  const onCloseSearch = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  const onCloseShortcutsPanel = useCallback(() => {
    setIsShortcutsPanelOpen(false);
  }, []);

  usePricingStatus({ applicationId, isDataLoaded });

  const { availableTabs } = useSelector(selectLoanApplicationControlUI);
  const isSavingPendingChanges = useSelector(selectIsSavingLoanApplicationChanges);
  const primaryBorrowerLastName = useSelector(selectPrimaryBorrowerLastName);
  const subjectProperty = useSelector(selectSubjectProperty);
  const deal = useSelector(selectDealFromApplication);
  const checklistResults = useSelector(selectLoanApplicationChecklistResultsList);
  const isUnsecuredLoan = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const isConstructionLoan = useSelector(selectIsConstructionFromPropertyAndLoanSummary);

  const recalculatingChecklistsKey = useMemo(
    () =>
      checklistResults
        .filter(({ status }) => CHECKLIST_RECALCULATING_STATUSES.includes(status?.type))
        .map(({ target }) => target?.checklistId)
        .sort()
        .join(),
    [checklistResults],
  );
  const hasRecalculationTimedOut = useChecklistRecalculationTimeout(recalculatingChecklistsKey);

  const { isViewAllowed: isDocumentsAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.DOCUMENTS),
  );
  const { isViewAllowed: isFormDeliveryViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.FORM_DELIVERY),
  );
  const { isViewAllowed: isCicHistoryViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_CIC_HISTORY),
  );
  const { isViewAllowed: isTasksAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.TASKS),
  );
  const { isViewAllowed: isConditionsAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_CONDITIONS),
  );
  const { isViewAllowed: isReportsAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.UNDERWRITING_REPORTS),
  );
  const { isViewAllowed: isLoanFeesAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_FEES),
  );
  const { isViewAllowed: isActivitiesTabAllowed } = useSelector(
    getMasterPermissionsToPage(COMPOSED_CLAIMS.LOAN_APPLICATION_ACTIVITIES),
  );
  const { isViewAllowed: isTagsAvailable } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_LOAN_TAG_ASSIGNEMENT),
  );
  const { isViewAllowed: isChecklistsAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_CHECKLISTS),
  );
  const isCallsAllowed = useSelector(selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_CALLS));
  const isInternalChatAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_CHAT),
  );
  const { isViewAllowed: isClosingAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_CLOSING),
  );
  const { isManageAllowed: isUnderwritingAccessAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_UNDERWRITING),
  );
  const { isViewAllowed: isLoanRolesTabAvailable } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_LOAN_ROLE_ASSIGNEMENT),
  );
  const { isViewAllowed: isServicingAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_SERVICING),
  );
  const { isViewAllowed: isPostClosingAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_POST_CLOSING),
  );
  const { isViewAllowed: isAiActivityAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_AI_ACTIVITY),
  );
  const { isViewAllowed: isConstructionViewAllowed, isEditAllowed: isConstructionEditAllowed } =
    useSelector(getMasterPermissionsToPage(PAGE_CODES.CONSTRUCTION));
  const isLoanExceptionsAllowed = useSelector(selectIsClaimAllowed('exceptionTracking.view'));

  const { isViewAllowed: isVendorRequestsClaimAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_VENDOR_REQUESTS),
  );
  const isVendorRequestsTabEnabled = useFeatureToggle(FeatureFlag.EnableVendorRequestsTab);
  const isVendorRequestsAllowed = isVendorRequestsClaimAllowed && isVendorRequestsTabEnabled;
  const { isViewAllowed: isContactsAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_CONTACTS),
  );

  const isHideLoanSpecificForm = useFeatureToggle(FeatureFlag.HideLoanSpecificForm);
  const isPaymentsAllowed = useFeatureToggle(FeatureFlag.EnablePaymentTab);
  const isFullChecklistRegenerationDisabled = useFeatureToggle(
    FeatureFlag.DisableFullChecklistRegeneration,
  );
  const isReadChecklistsFromFlatStoreEnabled = useFeatureToggle(
    FeatureFlag.ReadChecklistsFromFlatStore,
  );
  // Authentication (src/app/auth/Authentication.js) holds the whole route tree behind a splash
  // screen until feature flags finish loading, so this is already settled by the time the loan
  // editor mounts — a ref lets the root-read effect below read it without listing it as a
  // dependency, so a later (no-op) re-render of this value can't re-fire the effect (FH-3004).
  const isFullChecklistRegenerationDisabledRef = useRef(isFullChecklistRegenerationDisabled);

  const isAiChatAllowed = useSelector(selectIsAiChatEnabled);
  const isCrmEnabledForCompany = useSelector(selectIsCrmEnabledForCompany);
  const areMasterCompanyPropertiesSettled = useSelector(selectAreMasterCompanyPropertiesSettled);
  const isConstructionCoreEnabled = useFeatureToggle(FeatureFlag.EnableConstructionCore);
  const isConstructionAllowed = isConstructionCoreEnabled && isConstructionViewAllowed;
  const isRestyledUnderwritingTabEnabled = useFeatureToggle(
    FeatureFlag.EnableRestyledUnderwritingTab,
  );
  const isLegacyUnderwritingTabDisabled = useFeatureToggle(
    FeatureFlag.DisableLegacyUnderwritingTab,
  );

  const isRolesAllowed = isLoanRolesTabAvailable;

  const applicationStatus = deal?.applicationStatus;
  const isInitiatedByLeadStage = checkIsApplicationAtLeadStage(applicationStatus);
  const fromLoanId = navigationState?.loanId;
  const fromApplicationId = navigationState?.applicationId;

  const contentOptions = useMemo(() => {
    const isPaddingsDisabled = tabsWithoutHorPadding.includes(loanEditorActiveTab);

    return {
      disablePaddings: isPaddingsDisabled,
      isScrollable: !tabsWithoutContentScroll.includes(loanEditorActiveTab),
      ...(!isPaddingsDisabled && {
        customStyles: {
          paddingLeft: { xs: '8px', sm: '24px' },
          paddingRight: { xs: '8px', sm: '24px' },
          paddingBottom: { xs: '88px', sm: '88px', md: 0 },
        },
      }),
    };
  }, [loanEditorActiveTab]);

  const helmetProps = useMemo(() => {
    const primaryBorrowerLastNameString = primaryBorrowerLastName ?? '';
    const propertyAddress = _.get(subjectProperty, 'address.fullAddress', '');

    return { title: [primaryBorrowerLastNameString, propertyAddress].filter(Boolean).join(' - ') };
  }, [primaryBorrowerLastName, subjectProperty]);

  const activeTabOnInit = useMemo(() => {
    if (isInitialLoad.current && !_.isEmpty(availableTabs) && isDataLoaded) {
      // TOOD: refactor, find better way operating with config and availableTabs
      const initialTab = deepObjFind(
        availableTabs,
        LoanApplicationTabRouteParamInverted[
          LEGACY_LOAN_TAB_ROUTE_REDIRECTS[loanEditorActiveTab] ?? loanEditorActiveTab
        ],
      );

      return (
        LoanApplicationTabRouteParam[initialTab] || LoanApplicationTabRouteParam.PROPERTY_AND_LOAN
      );
    }
  }, [loanEditorActiveTab, availableTabs, isDataLoaded]);

  const isLoanEditorInIframe = isOpenInIframe || isFromLoansIframe.current;

  const isInvalidApplicationPathParams = !!(loanId || activeTabOnInit !== loanEditorActiveTab);

  const isInitialRedirectToApplicationPath =
    isDataLoaded && isInitialLoad.current && isInvalidApplicationPathParams;

  const { shortcutMap } = useMemo(() => buildShortcutMap({ availableTabs }), [availableTabs]);

  useEffect(() => {
    const isMac = navigator.platform.toUpperCase().includes('MAC');

    const updateModifierHeld = event => {
      const isModifierPressed = isMac ? event.metaKey : event.ctrlKey;

      setIsModifierHeld(isModifierPressed);
    };

    const handleBlur = () => {
      setIsModifierHeld(false);
    };

    document.addEventListener('keydown', updateModifierHeld, true);
    document.addEventListener('keyup', updateModifierHeld, true);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('keydown', updateModifierHeld, true);
      document.removeEventListener('keyup', updateModifierHeld, true);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = event => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const isModifierPressed = isMac ? event.metaKey : event.ctrlKey;

      if (isModifierPressed && event.key === 'k') {
        event.preventDefault();
        setSearchInitialQuery('');
        setIsSearchOpen(prev => !prev);
        setIsShortcutsPanelOpen(false);

        return;
      }

      if (isModifierPressed && event.key === '/') {
        event.preventDefault();
        setIsShortcutsPanelOpen(prev => !prev);
        setIsSearchOpen(false);

        return;
      }

      const isTextInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
      const isContentEditable = document.activeElement?.isContentEditable;

      if (event.key === '/' && !isModifierPressed && !isTextInput && !isContentEditable) {
        event.preventDefault();
        setSearchInitialQuery('/');
        setIsSearchOpen(true);
        setIsShortcutsPanelOpen(false);

        return;
      }

      const isDigitKey = event.key >= '1' && event.key <= '9';

      if (isModifierPressed && isDigitKey && shortcutMap[event.key]) {
        event.preventDefault();
        event.stopPropagation();

        const { tabRoute } = shortcutMap[event.key];

        setIsSearchOpen(false);

        history.push(
          LoansPaths.applicationManagement.generatePath({
            [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
            [LOANS_ROUTING_PARAMS.tab]: tabRoute,
          }),
        );
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [shortcutMap, applicationId, history]);

  useEffect(() => {
    dispatch(resetStateAction());

    return () => {
      dispatch(resetDealDataAction());
    };
  }, [dispatch]);

  useEffectSafeStrictMode(() => {
    // The execution-snapshot slice drives the loan-editor completeness "Needed" indicators
    // (DEV-4383), a loan-scoped read, so it must load for every user regardless of the
    // LOAN_CHECKLISTS admin-page view permission (DEV-4388). Kept on an applicationId-only
    // dep array so it fires exactly once per loan open and doesn't re-fire once companyId /
    // feature toggles settle after first paint (FH-3004).
    if (!applicationId) {
      return;
    }

    Promise.all([
      dispatch(
        getChecklistsExecutionSnapshotsAction({
          applicationId,
          includeResults: true,
          includeItems: true,
        }),
      ),
      ...getConditionalArray(isFullChecklistRegenerationDisabledRef.current, () => [
        dispatch(triggerOptionalChecklistRecalculation({ applicationId })),
      ]),
    ]).catch(Error);
  }, [applicationId, dispatch]);

  useEffectSafeStrictMode(() => {
    // With the legacy snapshots read, the hidden completeness-engine checklist ids must be
    // resolved separately. With the flat read, the root read above already self-populates this
    // same slice from its own payload (`getChecklistsExecutionSnapshotsAction`) — no second
    // request needed (FH-3004).
    if (!applicationId || isReadChecklistsFromFlatStoreEnabled) {
      return;
    }

    dispatch(getCompletenessChecklistIdsAction({ applicationId })).catch(Error);
  }, [applicationId, dispatch, isReadChecklistsFromFlatStoreEnabled]);

  useEffectSafeStrictMode(() => {
    // The company's DESIGNATED completeness checklist, which — when set — narrows the "Needed"
    // highlighting to exactly that checklist instead of every hidden one (DEV-4603).
    if (!companyId) {
      return;
    }

    dispatch(getCompletenessDesignationAction({ companyId })).catch(Error);
  }, [companyId, dispatch]);

  useEffectSafeStrictMode(() => {
    if (isDataLoaded) {
      return;
    }

    const markLoaded = () => setIsDataLoaded(true);

    if (applicationId) {
      dispatch(getDealDataByApplicationIdAction({ applicationId, history })).then(
        markLoaded,
        markLoaded,
      );

      return;
    }

    if (loanId && !fromApplicationId) {
      dispatch(getDealDataByLoanIdAction({ loanId })).then(markLoaded, markLoaded);

      return;
    }

    dispatch(setDealDataAction({ loanId: fromLoanId, applicationId: fromApplicationId }));

    markLoaded();
  }, [applicationId, loanId, dispatch, fromApplicationId, fromLoanId, isDataLoaded, history]);

  useEffect(() => {
    const checklistResultsSubtabs = getSubtabsFromChecklistResults(checklistResults, {
      isRecalculationHidden: hasRecalculationTimedOut,
    });

    const availableTabs = getAvailableTabs({
      // tabs
      isActivitiesTabAllowed,
      isTasksAllowed,
      isDocumentsAllowed,
      isFormDeliveryViewAllowed,
      isCicHistoryViewAllowed,
      isClosingAllowed,
      isPostClosingAllowed,
      isRestyledUnderwritingTabEnabled,
      isLegacyUnderwritingTabDisabled,
      // subtabs
      isCallsAllowed,
      isInternalChatAllowed,
      isRolesAllowed,
      isLoanFeesAllowed,
      isTagsAvailable,
      isChecklistsAllowed,
      isConditionsAllowed,
      isReportsAllowed,
      isKeyDatesAllowed: true,
      isUnderwritingAllowed: isUnderwritingAccessAllowed,
      isPaymentsAllowed,
      isServicingAllowed,
      isLoanExceptionsAllowed,
      isVendorRequestsAllowed,
      isContactsAllowed,
      isHideLoanSpecificForm,
      isAiChatAllowed,
      isAiActivityAllowed,
      isConstructionAllowed,
      isConstructionEditAllowed,
      // subtabs to inject
      checklistResultsSubtabs,
      // props
      isUnsecuredLoan,
      isConstructionLoan,
    });

    dispatch(setAvailableTabsAction(availableTabs));
  }, [
    dispatch,
    isDocumentsAllowed,
    isUnderwritingAccessAllowed,
    isTasksAllowed,
    isConditionsAllowed,
    isReportsAllowed,
    isLoanFeesAllowed,
    isActivitiesTabAllowed,
    isDataLoaded,
    isPaymentsAllowed,
    isRolesAllowed,
    isClosingAllowed,
    isPostClosingAllowed,
    isRestyledUnderwritingTabEnabled,
    isLegacyUnderwritingTabDisabled,
    isCallsAllowed,
    isTagsAvailable,
    isChecklistsAllowed,
    isHideLoanSpecificForm,
    isFormDeliveryViewAllowed,
    isCicHistoryViewAllowed,
    isInternalChatAllowed,
    isAiChatAllowed,
    isAiActivityAllowed,
    isServicingAllowed,
    isLoanExceptionsAllowed,
    isVendorRequestsAllowed,
    isContactsAllowed,
    checklistResults,
    isUnsecuredLoan,
    isConstructionAllowed,
    isConstructionEditAllowed,
    isConstructionLoan,
    hasRecalculationTimedOut,
  ]);

  useEffect(() => {
    if (!isLoanEditorInIframe) {
      return;
    }

    dispatch(setDefaultSettingsAction({ layout: emptyLayoutConfig }));
  }, [dispatch, isLoanEditorInIframe]);

  useEffect(() => {
    if (!isInitialRedirectToApplicationPath) {
      return;
    }

    isInitialLoad.current = false;
  }, [isInitialRedirectToApplicationPath]);

  const legacyTabRedirectRoute = LEGACY_LOAN_TAB_ROUTE_REDIRECTS[loanEditorActiveTab];

  if (legacyTabRedirectRoute && applicationId) {
    return (
      <Redirect
        to={LoansPaths.applicationManagement.generatePath({
          [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
          [LOANS_ROUTING_PARAMS.tab]: legacyTabRedirectRoute,
          [LOANS_ROUTING_PARAMS.subtab]: subtab,
        })}
      />
    );
  }

  if (!isDataLoaded) {
    return <Skeleton />;
  }

  // A lead-stage application has its own dedicated CRM lead workspace; the loan editor's
  // reduced lead view is retired. Redirect here so leads reaching the editor from any
  // surface (pipeline, search, a direct URL) land on the workspace. isInitiatedByLeadStage
  // can only be true once the deal (applicationStatus) has loaded. Companies that opted
  // out of CRM (EnableCrm=false) keep lead-stage applications in the loan editor — and
  // the routing decision waits for the company properties so a disabled company is never
  // bounced into the workspace it has turned off. Deliberately company-property-only
  // (NOT useIsCrmEnabled): this redirect never consulted the env feature flag, so gating
  // it on the FF would change lead routing in FF-off environments.
  if (isInitiatedByLeadStage) {
    if (!areMasterCompanyPropertiesSettled) {
      return <Skeleton />;
    }

    if (isCrmEnabledForCompany) {
      return <Redirect to={CrmPaths.leadWorkspace.generatePath({ applicationId })} />;
    }
  }

  if (isInitialRedirectToApplicationPath) {
    // The deal could not be resolved (e.g. an unknown loan id 404s), so there is no
    // applicationId to normalize the URL to. Fall back to the loans list instead of
    // calling generatePath with an undefined applicationId, which throws into the
    // global ErrorBoundary. The failure was already surfaced via the error toast.
    if (!deal?.applicationId) {
      return <Redirect to={LoansPaths.loans.generatePath()} />;
    }

    const applicationPathname = LoansPaths.applicationManagement.generatePath({
      [LOANS_ROUTING_PARAMS.applicationId]: deal.applicationId,
      [LOANS_ROUTING_PARAMS.tab]: activeTabOnInit,
      [LOANS_ROUTING_PARAMS.subtab]: subtab,
    });

    // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
    const to = {
      pathname: applicationPathname,
      state: { ...navigationState, pathFrom: pathname },
    };

    return <Redirect to={to} />;
  }

  return (
    <>
      <ContentLayout
        content={<LoanApplicationContent activeTab={loanEditorActiveTab} />}
        contentOptions={contentOptions}
        header={
          <MainHeaderContent
            isModifierHeld={isModifierHeld}
            shortcutPrefix={shortcutPrefix}
            onOpenSearch={onOpenSearch}
          />
        }
        headerOptions={headerOptions}
        helmetProps={helmetProps}
        sidebar={
          <LoanNavigationTabs
            applicationId={applicationId}
            availableTabs={availableTabs}
            isModifierHeld={isModifierHeld}
            shortcutPrefix={shortcutPrefix}
          />
        }
      />

      {isModifierHeld && shortcutPrefix && (
        <Box sx={shortcutsOverlayContainerStyles}>
          {ShortcutOverlayConfig.map(({ id, keycap, label, usePrefix }) => (
            <ShortcutOverlayItem
              key={id}
              keycap={usePrefix ? `${shortcutPrefix}${keycap}` : keycap}
              label={label}
            />
          ))}
        </Box>
      )}

      <UniversalSearch
        initialQuery={searchInitialQuery}
        isOpen={isSearchOpen}
        onClose={onCloseSearch}
      />
      <KeyboardShortcutsPanel isOpen={isShortcutsPanelOpen} onClose={onCloseShortcutsPanel} />
      <LoanEditorChatDrawer />
      <AiAssistant loanId={resolvedLoanId} />

      <SavingOverlay isFullScreen isOpen={isSavingPendingChanges} />
    </>
  );
};

LoanApplicationPage.propTypes = {
  isOpenInIframe: PropTypes.bool,
};

export default LoanApplicationPage;
